import React, { useState, useEffect, useRef } from 'react';

const Notepad = () => {
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('Untitled.txt');
  const [isSaved, setIsSaved] = useState(true);
  const [openFiles, setOpenFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const textareaRef = useRef(null);
  const localStorageKey = 'webOS_notes';

  // Autosave timer
  useEffect(() => {
    let saveTimer;
    
    if (autoSave && !isSaved && activeFile) {
      saveTimer = setTimeout(() => {
        saveCurrentFile();
      }, 3000); // Autosave after 3 seconds of inactivity
    }
    
    return () => {
      if (saveTimer) clearTimeout(saveTimer);
    };
  }, [content, fileName, isSaved, autoSave]);

  // Load saved files on component mount
  useEffect(() => {
    const savedNotes = localStorage.getItem(localStorageKey);
    if (savedNotes) {
      try {
        const parsedNotes = JSON.parse(savedNotes);
        setOpenFiles(parsedNotes);
        
        // Open the most recent file
        if (parsedNotes.length > 0) {
          const lastFile = parsedNotes[parsedNotes.length - 1];
          setActiveFile(lastFile.id);
          setFileName(lastFile.name);
          setContent(lastFile.content);
          setLastSaved(new Date(lastFile.dateModified));
        }
      } catch (error) {
        console.error("Error loading saved notes:", error);
        createWelcomeFile();
      }
    } else {
      createWelcomeFile();
    }
  }, []);

  // Create a welcome file for new users
  const createWelcomeFile = () => {
    const welcomeNote = {
      id: Date.now(),
      name: 'Welcome.txt',
      content: 'Welcome to Notepad!\n\nThis is a simple text editor for your WebOS.\n\nYou can:\n- Create new files with the New button\n- Save files with Ctrl+S or the Save button\n- Open multiple files in tabs\n- Edit text\n- Auto-save is enabled by default\n\nChanges are automatically saved to your browser\'s local storage.',
      dateModified: new Date().toISOString()
    };
    setOpenFiles([welcomeNote]);
    setActiveFile(welcomeNote.id);
    setFileName(welcomeNote.name);
    setContent(welcomeNote.content);
    localStorage.setItem(localStorageKey, JSON.stringify([welcomeNote]));
  };

  // Focus the textarea when component mounts or active file changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [activeFile]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Save with Ctrl+S
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        saveCurrentFile();
      }
      
      // New file with Ctrl+N
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        createNewFile();
      }
      
      // Close file with Ctrl+W
      if (e.ctrlKey && e.key === 'w' && activeFile) {
        e.preventDefault();
        closeFile(activeFile, new Event(''));
      }
      
      // Switch tabs with Ctrl+Tab and Ctrl+Shift+Tab
      if (e.ctrlKey && e.key === 'Tab') {
        e.preventDefault();
        if (openFiles.length > 1) {
          const currentIndex = openFiles.findIndex(file => file.id === activeFile);
          let nextIndex;
          
          if (e.shiftKey) {
            // Go to previous tab
            nextIndex = currentIndex > 0 ? currentIndex - 1 : openFiles.length - 1;
          } else {
            // Go to next tab
            nextIndex = currentIndex < openFiles.length - 1 ? currentIndex + 1 : 0;
          }
          
          switchToFile(openFiles[nextIndex].id);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openFiles, activeFile, isSaved]);

  // Handle content changes
  const handleContentChange = (e) => {
    setContent(e.target.value);
    setIsSaved(false);
  };

  // Handle file name change
  const handleFileNameChange = (e) => {
    setFileName(e.target.value);
    setIsSaved(false);
  };

  // Create a new file
  const createNewFile = () => {
    // Save current file before creating a new one
    if (activeFile && !isSaved) {
      saveCurrentFile();
    }
    
    const newFile = {
      id: Date.now(),
      name: 'Untitled.txt',
      content: '',
      dateModified: new Date().toISOString()
    };
    
    setOpenFiles([...openFiles, newFile]);
    setActiveFile(newFile.id);
    setFileName('Untitled.txt');
    setContent('');
    setIsSaved(true);
    setLastSaved(new Date());
  };

  // Save the current file
  const saveCurrentFile = () => {
    if (!activeFile) return;
    
    const now = new Date();
    const dateModified = now.toISOString();
    
    const updatedFiles = openFiles.map(file => 
      file.id === activeFile 
        ? { 
            ...file, 
            content, 
            name: fileName,
            dateModified
          } 
        : file
    );
    
    setOpenFiles(updatedFiles);
    localStorage.setItem(localStorageKey, JSON.stringify(updatedFiles));
    setIsSaved(true);
    setLastSaved(now);
  };

  // Switch to another open file
  const switchToFile = (fileId) => {
    // Save current file before switching
    if (activeFile && !isSaved) {
      saveCurrentFile();
    }
    
    const fileToOpen = openFiles.find(file => file.id === fileId);
    if (fileToOpen) {
      setActiveFile(fileId);
      setFileName(fileToOpen.name);
      setContent(fileToOpen.content);
      setIsSaved(true);
      setLastSaved(new Date(fileToOpen.dateModified));
    }
  };

  // Close a file
  const closeFile = (fileId, e) => {
    if (e) e.stopPropagation();
    
    // If closing the active file, save it first
    if (fileId === activeFile && !isSaved) {
      saveCurrentFile();
    }
    
    const updatedFiles = openFiles.filter(file => file.id !== fileId);
    setOpenFiles(updatedFiles);
    localStorage.setItem(localStorageKey, JSON.stringify(updatedFiles));
    
    // If we closed the active file, switch to another one
    if (fileId === activeFile && updatedFiles.length > 0) {
      const newActiveFile = updatedFiles[updatedFiles.length - 1];
      setActiveFile(newActiveFile.id);
      setFileName(newActiveFile.name);
      setContent(newActiveFile.content);
      setLastSaved(new Date(newActiveFile.dateModified));
    } else if (updatedFiles.length === 0) {
      // If no files left, create a new one
      createNewFile();
    }
  };

  // Export current file as download
  const exportFile = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowOptions(false);
  };

  // Import a file
  const importFile = (e) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.txt,.md,.js,.html,.css,.json';
    
    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const newFile = {
          id: Date.now(),
          name: file.name,
          content: e.target.result,
          dateModified: new Date().toISOString()
        };
        
        setOpenFiles([...openFiles, newFile]);
        setActiveFile(newFile.id);
        setFileName(newFile.name);
        setContent(newFile.content);
        setIsSaved(true);
        setLastSaved(new Date());
        
        // Save to localStorage
        localStorage.setItem(
          localStorageKey, 
          JSON.stringify([...openFiles, newFile])
        );
      };
      reader.readAsText(file);
    };
    
    fileInput.click();
    setShowOptions(false);
  };

  // Format the modified date
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleString();
  };

  // Get stats about the document
  const getDocumentStats = () => {
    const chars = content.length;
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    const lines = content.split('\n').length;
    return `${chars} characters | ${words} words | ${lines} lines`;
  };

  // Toggle autosave
  const toggleAutoSave = () => {
    setAutoSave(!autoSave);
    setShowOptions(false);
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      backgroundColor: 'rgba(10, 10, 20, 0.2)',
      backdropFilter: 'blur(10px)',
      color: '#e0e0e0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    }}>
      {/* Toolbar */}
      <div style={{ 
        display: 'flex', 
        padding: '8px', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: 'rgba(20, 20, 40, 0.3)',
        backdropFilter: 'blur(15px)',
        gap: '8px'
      }}>
        <button 
          onClick={createNewFile}
          title="New File (Ctrl+N)"
          style={{
            backgroundColor: 'transparent',
            color: '#e0e0e0',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <i className="fas fa-file"></i>
          <span>New</span>
        </button>
        
        <button 
          onClick={saveCurrentFile}
          disabled={isSaved}
          title="Save (Ctrl+S)"
          style={{
            backgroundColor: 'transparent',
            color: isSaved ? 'rgba(255, 255, 255, 0.3)' : '#e0e0e0',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 10px',
            cursor: isSaved ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => !isSaved && (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')}
          onMouseOut={(e) => !isSaved && (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <i className="fas fa-save"></i>
          <span>Save</span>
        </button>
        
        <div style={{ position: 'relative' }}>
          <button 
            onClick={() => setShowOptions(!showOptions)}
            title="More Options"
            style={{
              backgroundColor: 'transparent',
              color: '#e0e0e0',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <i className="fas fa-ellipsis-h"></i>
            <span>Options</span>
          </button>
          
          {showOptions && (
            <div 
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 100,
                backgroundColor: 'rgba(20, 20, 40, 0.8)',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '5px 0',
                minWidth: '180px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div 
                style={{
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => {
                  setContent(''); 
                  setIsSaved(false);
                  setShowOptions(false);
                }}
              >
                <i className="fas fa-trash-alt" style={{ width: '16px' }}></i>
                Clear Content
              </div>
              
              <div 
                style={{
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => {
                  navigator.clipboard.writeText(content);
                  setShowOptions(false);
                }}
              >
                <i className="fas fa-copy" style={{ width: '16px' }}></i>
                Copy All
              </div>
              
              <div 
                style={{
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={exportFile}
              >
                <i className="fas fa-download" style={{ width: '16px' }}></i>
                Export File
              </div>
              
              <div 
                style={{
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={importFile}
              >
                <i className="fas fa-upload" style={{ width: '16px' }}></i>
                Import File
              </div>
              
              <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '5px 0' }}></div>
              
              <div 
                style={{
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={toggleAutoSave}
              >
                <i className={`fas ${autoSave ? 'fa-check-square' : 'fa-square'}`} style={{ width: '16px' }}></i>
                Auto-Save
              </div>
              
              <div 
                style={{
                  padding: '8px 15px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => {
                  setShowAbout(true);
                  setShowOptions(false);
                }}
              >
                <i className="fas fa-info-circle" style={{ width: '16px' }}></i>
                About Notepad
              </div>
            </div>
          )}
        </div>
        
        <div style={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center',
          marginLeft: '15px' 
        }}>
          <input 
            type="text" 
            value={fileName}
            onChange={handleFileNameChange}
            onBlur={() => {
              if (!isSaved) saveCurrentFile();
            }}
            style={{
              backgroundColor: 'rgba(20, 20, 40, 0.3)',
              color: '#e0e0e0',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              padding: '6px 10px',
              width: '200px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)'}
            onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
          />
          {!isSaved && (
            <span style={{ marginLeft: '8px', color: '#aaa', fontSize: '14px' }}>
              <i className="fas fa-circle" style={{ fontSize: '8px' }}></i>
            </span>
          )}
        </div>
      </div>
      
      {/* File tabs */}
      <div style={{ 
        display: 'flex', 
        overflowX: 'auto',
        backgroundColor: 'rgba(20, 20, 40, 0.2)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {openFiles.map(file => (
          <div 
            key={file.id}
            onClick={() => switchToFile(file.id)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderRight: '1px solid rgba(255, 255, 255, 0.05)',
              backgroundColor: file.id === activeFile ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
              maxWidth: '200px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              if (file.id !== activeFile) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseOut={(e) => {
              if (file.id !== activeFile) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <i className="fas fa-file-alt" style={{ fontSize: '14px', color: '#aaa' }}></i>
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              fontSize: '14px'
            }}>
              {file.name}
              {openFiles.find(f => f.id === file.id) && !openFiles.find(f => f.id === file.id).isSaved && ' *'}
            </div>
            <button 
              onClick={(e) => closeFile(file.id, e)}
              title="Close File (Ctrl+W)"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#777',
                width: '22px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '11px',
                fontSize: '10px',
                cursor: 'pointer',
                marginLeft: '4px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 50, 50, 0.8)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#777';
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        ))}
      </div>
      
      {/* Text editor */}
      <textarea
        ref={textareaRef}
        value={content}
        onChange={handleContentChange}
        style={{
          flexGrow: 1,
          backgroundColor: 'rgba(10, 10, 20, 0.3)',
          color: '#e0e0e0',
          border: 'none',
          padding: '15px',
          resize: 'none',
          outline: 'none',
          fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
          fontSize: '15px',
          lineHeight: '1.5',
          caretColor: '#fff',
          tabSize: 4
        }}
        placeholder="Start typing..."
        spellCheck="false"
      />
      
      {/* Status bar */}
      <div style={{ 
        padding: '6px 15px', 
        backgroundColor: 'rgba(20, 20, 40, 0.3)',
        backdropFilter: 'blur(15px)',
        fontSize: '12px',
        color: '#aaa',
        display: 'flex',
        justifyContent: 'space-between',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div>
          {getDocumentStats()}
        </div>
        <div>
          {autoSave ? 'Auto-save enabled' : 'Auto-save disabled'} 
          {lastSaved && ` | Last saved: ${formatDate(lastSaved)}`}
        </div>
      </div>
      
      {/* About dialog */}
      {showAbout && (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'rgba(20, 20, 40, 0.8)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.5)',
            borderRadius: '12px',
            width: '400px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '14px 18px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>About Notepad</h3>
              <button 
                onClick={() => setShowAbout(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#aaa',
                  cursor: 'pointer',
                  fontSize: '16px',
                  width: '28px',
                  height: '28px',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 50, 50, 0.8)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#aaa';
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={{ padding: '18px', fontSize: '14px', lineHeight: '1.6' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '8px', 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px'
                }}>
                  <i className="fas fa-file-alt" style={{ fontSize: '24px', color: '#e0e0e0' }}></i>
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Notepad for WebOS</h4>
                  <p style={{ margin: 0, color: '#aaa' }}>Version 1.0</p>
                </div>
              </div>
              <p>A lightweight text editor with multi-tab support and automatic saving to browser storage.</p>
              <p style={{ marginBottom: '6px' }}>Features:</p>
              <ul style={{ paddingLeft: '20px', margin: '0 0 15px 0' }}>
                <li>Multiple document tabs</li>
                <li>Auto-save functionality</li>
                <li>Local storage persistence</li>
                <li>Import and export files</li>
                <li>Keyboard shortcuts</li>
              </ul>
            </div>
            <div style={{ 
              padding: '12px 18px', 
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                onClick={() => setShowAbout(false)}
                style={{
                  backgroundColor: 'rgba(80, 120, 220, 0.3)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(80, 120, 220, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(80, 120, 220, 0.3)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notepad; 